import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api/client.js';
import type { AgentShiftState, AgentState, AgentPauseReason, WrapUpRule } from '@arcnet/contracts';

/**
 * The shift: logging on, what state it is in, and saying it is still there (API agentshifts.ts).
 *
 * The workspace is the only thing that knows a person is at their desk, so it is the workspace
 * that says so -- once a minute while it is open. A shift nobody ends is closed by the sweep at
 * the time it was last heard from, so a closed laptop does not become a ten-hour day.
 *
 * ON A CALL IS NOT A STATE SOMEBODY CHOOSES. The phone knows, so the phone sets it, and going
 * back afterwards returns to whatever was chosen before the call -- ready, or the pause they
 * were already on when it came in.
 */

/** What the platform says a shift is, on every beat. */
type Beat =
  | { open: false }
  | { open: true; shift_id: string; started_at: string; state: AgentState; reason: string | null;
      state_since: string };

export interface ShiftApi {
  loaded: boolean;
  /** The wrap-up rule that applies to this agent: how long, whether to mark, and from where. */
  wrapUp: WrapUpRule;
  /** Shorthand for wrapUp.required, which is what most of the screen asks. */
  requireWrapUp: boolean;
  /** Set when the shift ended somewhere other than this screen, until it is logged on again. */
  endedElsewhere: boolean;
  /** Null when nobody is logged on at this seat. */
  shiftId: string | null;
  startedAt: string | null;
  state: AgentState;
  reason: string | null;
  stateSince: string | null;
  reasons: AgentPauseReason[];
  busy: boolean;
  error: string | null;
  logOn: () => Promise<void>;
  logOff: () => Promise<void>;
  setState: (state: AgentState, reason?: string | null) => Promise<void>;
}

export function useShift(tenantId: string, extensionId: string, onCall: boolean): ShiftApi {
  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}/shift`;
  const [s, setS] = useState<AgentShiftState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [endedElsewhere, setEndedElsewhere] = useState(false);
  /** What to go back to when a call ends: the state that was chosen, not the one the call set. */
  const chosen = useRef<{ state: AgentState; reason: string | null }>({ state: 'ready', reason: null });

  const load = useCallback(async () => {
    const r = await api.get<AgentShiftState>(base);
    setS(r);
    if (r.id !== null && r.state !== 'on_call') chosen.current = { state: r.state, reason: r.reason };
  }, [base]);

  useEffect(() => { void load().catch(() => setS(null)); }, [load]);

  const open = s !== null && s.id !== null;

  /**
   * While the workspace is open, it says so -- and listens to the answer.
   *
   * The beat carries the shift as the platform has it, so a shift ended by the sweep, by a
   * supervisor, or by the same person on another machine corrects this screen within the minute
   * instead of leaving it showing Ready to somebody who was logged out an hour ago.
   */
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => {
      void api.post<Beat>(`${base}/heartbeat`, {}).then((beat) => {
        if (!beat.open) {
          setEndedElsewhere(true);
          setS((prev) => (prev
            ? { id: null, reasons: prev.reasons, require_wrap_up: prev.require_wrap_up,
                wrap_up: prev.wrap_up }
            : prev));
          return;
        }
        chosen.current = beat.state === 'on_call'
          ? chosen.current : { state: beat.state, reason: beat.reason };
        setS((prev) => (prev && prev.id !== null
          ? { ...prev, state: beat.state, reason: beat.reason, state_since: beat.state_since }
          : prev));
      }).catch(() => undefined);
    }, 60_000);
    return () => clearInterval(t);
  }, [base, open]);

  const put = useCallback(async (state: AgentState, reason: string | null) => {
    await api.put(`${base}/state`, { state, ...(reason ? { reason } : {}) });
    setS((prev) => (prev && prev.id !== null
      ? { ...prev, state, reason, state_since: new Date().toISOString() } : prev));
  }, [base]);

  /**
   * The phone's doing, not the person's.
   *
   * A call ending returns to whatever was chosen -- unless the account holds its agents in
   * wrap-up (0110), in which case the call ends into wrap-up and the person leaves it by marking
   * the call. A pause is still theirs to take from there; only Ready waits.
   */
  useEffect(() => {
    if (!open || !s || s.id === null) return;
    const wrapping = s.state === 'wrap_up';
    const want: AgentState = onCall ? 'on_call'
      : (s.wrap_up.required || s.wrap_up.seconds > 0) && s.state === 'on_call' ? 'wrap_up'
        : wrapping ? 'wrap_up' : chosen.current.state;
    const reason = want === 'not_ready' ? chosen.current.reason : null;
    if (s.state !== want || s.reason !== reason) {
      void put(want, reason).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCall, open]);

  const guard = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally { setBusy(false); }
  };

  return {
    loaded: s !== null,
    wrapUp: s?.wrap_up ?? { seconds: 0, required: false, seconds_from: 'account', required_from: 'account' },
    requireWrapUp: s?.wrap_up.required ?? false,
    endedElsewhere,
    shiftId: s && s.id !== null ? s.id : null,
    startedAt: s && s.id !== null ? s.started_at : null,
    state: s && s.id !== null ? s.state : 'ready',
    reason: s && s.id !== null ? s.reason : null,
    stateSince: s && s.id !== null ? s.state_since : null,
    reasons: s?.reasons ?? [],
    busy,
    error,
    logOn: () => guard(async () => { setEndedElsewhere(false); await api.post(base, {}); await load(); }),
    logOff: () => guard(async () => { await api.delete(base); await load(); }),
    setState: (state, reason = null) => guard(async () => {
      chosen.current = { state, reason };
      await put(state, reason);
    }),
  };
}
